import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, ShoppingBag, ArrowRight, Users, Send, Star } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminListProducts, adminListCarriers, adminListOrders } from "../lib/api";

const AdminHub = () => {
  const [stats, setStats] = useState({ products: 0, perfumes: 0, watches: 0, carriers: 0 });

  useEffect(() => {
    Promise.all([adminListProducts(), adminListCarriers()])
      .then(([products, carriers]) => {
        setStats({
          products: products.length,
          perfumes: products.filter((p) => (p.product_type || "perfume") === "perfume").length,
          watches: products.filter((p) => p.product_type === "watch").length,
          carriers: carriers.filter((c) => c.active).length,
        });
      })
      .catch(() => {});
  }, []);

  const tiles = [
    {
      to: "/admin/products",
      icon: Package,
      label: "Produits",
      sub: `${stats.products} articles · ${stats.perfumes} parfums · ${stats.watches} montres`,
      desc: "Ajouter, modifier, supprimer vos produits. Gérer prix, photos, descriptions et stock.",
      testid: "tile-products",
    },
    {
      to: "/admin/shipping",
      icon: Truck,
      label: "Livraison",
      sub: `${stats.carriers} transporteurs actifs`,
      desc: "Configurer les transporteurs, tarifs et délais de livraison.",
      testid: "tile-shipping",
    },
    {
      to: "/admin/boxtal",
      icon: Send,
      label: "Boxtal",
      sub: "Étiquettes automatiques",
      desc: "Connecter votre application Boxtal pour générer les étiquettes Colissimo, Chronopost, Mondial Relay et Colis Privé en un clic.",
      testid: "tile-boxtal",
    },
    {
      to: "/admin/reviews",
      icon: Star,
      label: "Avis clients",
      sub: "Modération achats vérifiés",
      desc: "Valider ou rejeter les avis soumis par vos clients après livraison. Les avis approuvés apparaissent sur la fiche produit avec le badge Achat vérifié.",
      testid: "tile-reviews",
    },
  ];

  return (
    <AdminLayout title="Tableau de bord" subtitle="Bienvenue dans votre espace de gestion. Choisissez la section à administrer.">
      {/* Stats overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
        <div className="border border-white/10 p-6">
          <p className="label-caps text-[color:var(--gold)] text-[9px] mb-2">Catalogue</p>
          <p className="font-serif text-4xl md:text-5xl text-white">{stats.products}</p>
          <p className="text-xs text-white/40 mt-2">articles au total</p>
        </div>
        <div className="border border-white/10 p-6">
          <p className="label-caps text-[color:var(--gold)] text-[9px] mb-2">Parfums</p>
          <p className="font-serif text-4xl md:text-5xl text-white">{stats.perfumes}</p>
          <p className="text-xs text-white/40 mt-2">fragrances en ligne</p>
        </div>
        <div className="border border-white/10 p-6">
          <p className="label-caps text-[color:var(--gold)] text-[9px] mb-2">Montres</p>
          <p className="font-serif text-4xl md:text-5xl text-white">{stats.watches}</p>
          <p className="text-xs text-white/40 mt-2">pièces horlogères</p>
        </div>
        <div className="border border-white/10 p-6">
          <p className="label-caps text-[color:var(--gold)] text-[9px] mb-2">Livraison</p>
          <p className="font-serif text-4xl md:text-5xl text-white">{stats.carriers}</p>
          <p className="text-xs text-white/40 mt-2">transporteurs actifs</p>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="group border border-white/10 hover:border-[color:var(--gold)] p-8 md:p-10 transition-all block"
            >
              <div className="flex items-start justify-between gap-6 mb-8">
                <Icon size={28} strokeWidth={1.2} className="text-[color:var(--gold)]" />
                <ArrowRight size={16} className="text-white/40 group-hover:text-[color:var(--gold)] group-hover:translate-x-1 transition-all" />
              </div>
              <p className="label-caps text-[color:var(--gold)] text-[10px] mb-3">{t.sub}</p>
              <h3 className="font-serif text-3xl md:text-4xl text-white mb-4">{t.label}</h3>
              <p className="text-white/50 font-light text-sm leading-relaxed">{t.desc}</p>
            </Link>
          );
        })}
      </div>

      <p className="text-white/40 text-xs italic mt-16 max-w-2xl">
        Toutes les modifications sont appliquées immédiatement sur le site public. Les commandes de vos clients sont conservées séparément et ne sont jamais affectées par les changements du catalogue.
      </p>
    </AdminLayout>
  );
};

export default AdminHub;
